/**
 * Future Cartridge dialogue overlay — Phaser-wired DOM shell.
 *
 * The state machine and luminance lookup live in
 *   apps/game/src/ui/dialogueOverlayLogic.ts (pure)
 *   apps/game/src/data/mapLuminance.ts        (pure)
 *
 * This file is the impure consumer — owns the <div> mounted into #game,
 * applies/removes the dialog-luma-* body class on mount, wires
 * gestureRouter.onTap for advance, handles viewport resize, and
 * subscribes to Phaser SLEEP/WAKE so the DOM hides cleanly when the
 * scene swaps out for battle/draft/menu.
 *
 * Mirrors PartyOrbs.ts's pattern (DOM child of #game, rAF-coalesced
 * resize, SLEEP/WAKE listeners, disposer-returning factory).
 *
 * BattleScene's dialogue stays on its existing Phaser-side path until
 * PR 3; this shell is WorldScene-only.
 */
import Phaser from 'phaser';
import { onTap } from './gestureRouter';
import { Z_DIALOGUE } from './zLayers';
import { luminanceFor, type MapLuminance } from '../data/mapLuminance';
import { effectsReduced } from '../render/motion';
import {
  type DialogState,
  reduceDialog,
  shouldSuppress,
  nextStateOnSleep,
  nextStateOnWake,
} from './dialogueOverlayLogic';

const PARENT_ID = 'game';
const ENTRY_MS = 200;
const EXIT_MS = 150;
/** Panel top edge as a fraction of canvas height — top of the lower third. */
const TOP_FRACTION = 0.66;
/** Grace window after show() during which tap-to-advance events are
 *  ignored. iOS Safari fires a synthetic click event ~300 ms after
 *  touchend; if the overlay mounted in the meantime, that click can
 *  land on the dialogue's hit area and trigger advance — closing a
 *  1-page dialogue (Mom, signs) on what looked like one tap. The
 *  grace lets the in-flight synthetic event drain. Tuned 50 ms above
 *  the documented 300 ms click delay. */
const TAP_GRACE_MS = 350;

export interface DialogueShowOpts {
  speakerName?: string;
  /** Region name (canonical short form, e.g. "PalletTown"). Resolves to
   *  a luminance tier via mapLuminance.luminanceFor() — drives the
   *  dialog-luma-* body class for the 3-tier auto-adapt. */
  region?: string;
  /** Direct luminance override. If both region and luminance are
   *  provided, this wins. */
  luminance?: MapLuminance;
}

export interface DialogueOverlayHandle {
  /** Open with one or more pages. Replaces any existing content. */
  show(pages: string[], opts?: DialogueShowOpts): void;
  /** Advance to the next page; on the last page, closes. */
  advance(): void;
  /** Close immediately (triggers exit animation). */
  close(): void;
  /** True if any page is being rendered (opening / open / closing). */
  isOpen(): boolean;
  /** External suppression flag (PartySheet open). Hides DOM without
   *  transitioning state; on unsuppress the prior page is restored. */
  setExternallySuppressed(suppressed: boolean): void;
  /** Subscribe to close. Fires once per closeDialogue() call AFTER the
   *  exit animation completes. Returns a disposer. */
  onClosed(handler: () => void): () => void;
  /** Subscribe to advance. Fires on each successful advance() that
   *  didn't close the overlay (i.e., moved to a next page). */
  onAdvanced(handler: () => void): () => void;
  /** Unmount + remove all listeners. Idempotent. */
  destroy(): void;
}

function prefersReducedMotion(): boolean {
  // Policy 2026-08-02: the OS setting is ignored — the in-app Settings
  // toggle is the only reduction path (see state/motionPrefs.ts).
  return effectsReduced();
}

/**
 * Mount the dialogue overlay onto the Phaser canvas's parent (#game).
 * Returns a handle the WorldScene drives via show/advance/close.
 */
export function mountDialogueOverlay(scene: Phaser.Scene): DialogueOverlayHandle {
  const parent = document.getElementById(PARENT_ID) ?? document.body;

  // ─── DOM construction ────────────────────────────────────────────────
  const el = document.createElement('div');
  el.className = 'dialogue-overlay';
  el.style.zIndex = String(Z_DIALOGUE);
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-live', 'polite');

  const speakerEl = document.createElement('div');
  speakerEl.className = 'dialogue-overlay__speaker';
  speakerEl.style.fontFamily = '"DM Sans Variable", "DM Sans", system-ui, -apple-system, sans-serif';
  speakerEl.style.fontSize = '13px';
  speakerEl.style.fontWeight = '600';
  speakerEl.style.letterSpacing = '0.02em';
  speakerEl.style.textTransform = 'uppercase';
  speakerEl.style.color = 'var(--text-secondary)';
  speakerEl.style.marginBottom = '6px';
  el.appendChild(speakerEl);

  const bodyEl = document.createElement('div');
  bodyEl.className = 'dialogue-overlay__body';
  bodyEl.style.fontFamily = '"DM Sans Variable", "DM Sans", system-ui, -apple-system, sans-serif';
  bodyEl.style.fontSize = '17px';
  bodyEl.style.fontWeight = '500';
  bodyEl.style.lineHeight = '1.4';
  bodyEl.style.color = 'var(--text-primary)';
  bodyEl.style.minHeight = '48px';
  el.appendChild(bodyEl);

  const arrowEl = document.createElement('div');
  arrowEl.className = 'dialogue-overlay__continuation';
  arrowEl.textContent = '▼';
  arrowEl.style.position = 'absolute';
  arrowEl.style.right = '12px';
  arrowEl.style.bottom = '8px';
  arrowEl.style.fontFamily = '"DM Sans Variable", "DM Sans", system-ui, sans-serif';
  arrowEl.style.fontSize = '14px';
  arrowEl.style.color = 'var(--text-tertiary)';
  arrowEl.style.pointerEvents = 'none';
  el.appendChild(arrowEl);

  el.style.display = 'none';
  parent.appendChild(el);

  // ─── Internal state ──────────────────────────────────────────────────
  let state: DialogState = 'closed';
  let pages: string[] = [];
  let pageIdx = 0;
  let region: string | undefined;
  let directLuma: MapLuminance | undefined;
  let appliedLumaClass: string | null = null;
  let partySheetOpen = false;
  let sleeping = false;
  let entryTimer: ReturnType<typeof setTimeout> | null = null;
  let exitTimer: ReturnType<typeof setTimeout> | null = null;
  let openedAt = 0;
  let tapDisposer: (() => void) | null = null;
  const closedListeners = new Set<() => void>();
  const advancedListeners = new Set<() => void>();

  // ─── Apply state to DOM ──────────────────────────────────────────────
  function applyVisibility(): void {
    el.style.display = shouldSuppress(state, partySheetOpen, sleeping) ? 'none' : 'block';
  }

  function applyLuminance(luma: MapLuminance | null): void {
    if (appliedLumaClass) {
      document.body.classList.remove(appliedLumaClass);
      appliedLumaClass = null;
    }
    if (luma) {
      const cls = `dialog-luma-${luma}`;
      document.body.classList.add(cls);
      appliedLumaClass = cls;
    }
  }

  function resolveLuma(): MapLuminance {
    if (directLuma) return directLuma;
    if (region) return luminanceFor(region);
    return 'mid';
  }

  function renderPage(): void {
    bodyEl.textContent = pages[pageIdx] ?? '';
    arrowEl.style.visibility = pageIdx < pages.length - 1 ? 'visible' : 'hidden';
  }

  function setEntering(): void {
    el.classList.remove('is-exiting');
    el.classList.add('is-entering');
    if (entryTimer) clearTimeout(entryTimer);
    entryTimer = setTimeout(() => {
      state = reduceDialog(state, 'ENTRY_COMPLETE');
      el.classList.remove('is-entering');
      entryTimer = null;
    }, prefersReducedMotion() ? 0 : ENTRY_MS);
  }

  function setExiting(): void {
    el.classList.remove('is-entering');
    el.classList.add('is-exiting');
    if (exitTimer) clearTimeout(exitTimer);
    exitTimer = setTimeout(() => {
      state = reduceDialog(state, 'EXIT_COMPLETE');
      el.classList.remove('is-exiting');
      exitTimer = null;
      applyVisibility();
      applyLuminance(null);
      const handlers = Array.from(closedListeners);
      for (const h of handlers) h();
    }, prefersReducedMotion() ? 0 : EXIT_MS);
  }

  // ─── Resize handling — rAF-coalesced ─────────────────────────────────
  let resizeQueued = false;
  function position(): void {
    const w = parent.clientWidth;
    const h = parent.clientHeight;
    el.style.left = `${Math.round(w * 0.06)}px`;
    el.style.width = `${Math.round(w * 0.88)}px`;
    // Top edge of the panel sits at the top of the canvas's lower third.
    // The world pauses during dialogue, so the panel can own mid-screen
    // instead of hugging the bottom edge (where it fought the orb strip).
    const top = Math.round(h * TOP_FRACTION);
    el.style.top = `${top}px`;
    el.style.bottom = 'auto';
    // Bottom clamp: a top-anchored panel grows downward, and #game is
    // fixed inset-0 with overflow hidden — an unusually long page (the
    // draft-failure rescue text) on a short landscape phone would clip
    // past the canvas bottom with no scroll recovery. Cap the panel a
    // margin above the bottom edge and let the body scroll instead.
    el.style.maxHeight = `${h - top - 12}px`;
    el.style.overflowY = 'auto';
  }
  function onResize(): void {
    if (resizeQueued) return;
    resizeQueued = true;
    requestAnimationFrame(() => {
      resizeQueued = false;
      position();
    });
  }
  position();
  window.visualViewport?.addEventListener('resize', onResize);
  window.addEventListener('resize', onResize);
  window.addEventListener('orientationchange', onResize);

  // ─── Tap-to-advance via gestureRouter ────────────────────────────────
  // Re-created on every show() so no stale pointer-event state leaks
  // between dialogue cycles. Callback guards on state === 'open' AND
  // a TAP_GRACE_MS window since show() so iOS Safari's synthetic
  // click event (~300 ms after touchend) can't auto-close 1-page
  // dialogues by firing advance.
  function attachTap(): void {
    if (tapDisposer) return;
    tapDisposer = onTap(el, {}, (e) => {
      if (state !== 'open') return;
      // iOS Safari synthetic click fires as a MOUSE pointer event
      // ~300 ms after touchend; real user taps fire as TOUCH events.
      // Grace applies only to mouse so the user's tap to dismiss
      // isn't swallowed if they're fast.
      if (e.pointerType !== 'touch' && performance.now() - openedAt < TAP_GRACE_MS) return;
      handle.advance();
    });
  }
  function detachTap(): void {
    if (tapDisposer) {
      tapDisposer();
      tapDisposer = null;
    }
  }
  attachTap();

  // ─── Phaser SLEEP/WAKE lifecycle ─────────────────────────────────────
  const onSleep = (): void => {
    sleeping = true;
    state = nextStateOnSleep(state);
    if (entryTimer) {
      clearTimeout(entryTimer);
      entryTimer = null;
    }
    if (exitTimer) {
      clearTimeout(exitTimer);
      exitTimer = null;
    }
    el.classList.remove('is-entering', 'is-exiting');
    applyLuminance(null);
    applyVisibility();
  };
  const onWake = (): void => {
    sleeping = false;
    state = nextStateOnWake(state);
    applyVisibility();
  };
  scene.events.on(Phaser.Scenes.Events.SLEEP, onSleep);
  scene.events.on(Phaser.Scenes.Events.WAKE, onWake);

  // ─── Public handle ───────────────────────────────────────────────────
  const handle: DialogueOverlayHandle = {
    show(nextPages, opts): void {
      // Defensive: clear any in-flight timers + visual classes from a
      // prior show/close cycle so a stale exit timer can't fire onto
      // the new dialogue mid-entry. Also re-create the tap listener
      // so no stale pointer-event state leaks across cycles.
      if (entryTimer) {
        clearTimeout(entryTimer);
        entryTimer = null;
      }
      if (exitTimer) {
        clearTimeout(exitTimer);
        exitTimer = null;
      }
      el.classList.remove('is-entering', 'is-exiting');
      detachTap();
      attachTap();
      openedAt = performance.now();

      pages = nextPages.length > 0 ? nextPages : ['...'];
      pageIdx = 0;
      region = opts?.region;
      directLuma = opts?.luminance;
      applyLuminance(resolveLuma());
      renderPage();
      // Speaker line shows when provided AND a real name (not just whitespace).
      const name = opts?.speakerName?.trim();
      if (name) {
        speakerEl.textContent = name;
        speakerEl.style.display = 'block';
      } else {
        speakerEl.textContent = '';
        speakerEl.style.display = 'none';
      }
      state = reduceDialog(state, 'OPEN');
      applyVisibility();
      position();
      setEntering();
    },
    advance(): void {
      if (state !== 'open' && state !== 'opening') return;
      pageIdx++;
      if (pageIdx >= pages.length) {
        handle.close();
        return;
      }
      renderPage();
      const handlers = Array.from(advancedListeners);
      for (const h of handlers) h();
    },
    close(): void {
      if (state === 'closed' || state === 'closing' || state === 'suppressed') return;
      state = reduceDialog(state, 'CLOSE');
      setExiting();
    },
    isOpen(): boolean {
      return state === 'opening' || state === 'open' || state === 'closing';
    },
    setExternallySuppressed(suppressed): void {
      partySheetOpen = suppressed;
      applyVisibility();
    },
    onClosed(h): () => void {
      closedListeners.add(h);
      return () => closedListeners.delete(h);
    },
    onAdvanced(h): () => void {
      advancedListeners.add(h);
      return () => advancedListeners.delete(h);
    },
    destroy(): void {
      if (entryTimer) clearTimeout(entryTimer);
      if (exitTimer) clearTimeout(exitTimer);
      detachTap();
      scene.events.off(Phaser.Scenes.Events.SLEEP, onSleep);
      scene.events.off(Phaser.Scenes.Events.WAKE, onWake);
      window.visualViewport?.removeEventListener('resize', onResize);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      applyLuminance(null);
      el.remove();
      closedListeners.clear();
      advancedListeners.clear();
    },
  };

  return handle;
}
